import { useRef } from "react";
import { Search } from "lucide-react";
import { isMacOs } from "react-device-detect";
import { useHotkeys } from "react-hotkeys-hook";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/_atoms/button";
import { Input } from "@/components/_atoms/input";
import { IconXCircleFilled } from "@/components/_icons/IconXCircleFilled";

interface QuickSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onClear?: () => void;
}

export const QuickSearchInput = ({ value, onChange, onClear = () => onChange("") }: QuickSearchInputProps) => {
  const { t } = useTranslation("common");
  const inputRef = useRef<HTMLInputElement>(null);

  useHotkeys("mod+f", () => inputRef.current?.focus(), { preventDefault: true });

  return (
    <div className="group relative flex h-7 min-w-0 flex-1 items-center rounded-md border border-input bg-background focus-within:z-10 focus-within:ring-2 focus-within:ring-focus">
      <Search
        className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
        aria-hidden="true"
      />
      <Input
        ref={inputRef}
        value={value}
        onChange={(event) => onChange(event.currentTarget.value)}
        placeholder={t("search.placeholder", { key: isMacOs ? "⌘" : "Ctrl" })}
        aria-label={t("search.label")}
        className="h-full w-full border-0 bg-transparent pl-8 pr-8 shadow-none focus-visible:outline-none placeholder:text-xs"
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            onClear();
          }
        }}
      />
      {value !== "" && (
        <Button
          variant="ghost"
          size="icon"
          className="invisible absolute right-1.5 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full p-0 hover:bg-transparent focus-visible:visible group-focus-within:visible group-hover:visible"
          onClick={onClear}
          aria-label={t("search.clear")}
        >
          <IconXCircleFilled className="h-3 w-3 text-foreground/60" aria-hidden="true" />
        </Button>
      )}
    </div>
  );
};
